import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { PayrollService } from 'src/app/services/payroll.service';
import { UtilsService } from 'src/app/services/utils.service';

interface PayrollConcept {
    code?: string;
    concept?: string;
    quantity?: number;
    amount?: number;
}

@Component({
    selector: 'app-payroll-detail',
    templateUrl: './payroll-detail.component.html',
    styleUrls: ['./payroll-detail.component.css']
})
export class PayrollDetailComponent implements OnInit {

    employeeId!: number;
    payrollId!: number;
    payroll: any = null;

    loading = false;
    loadError = false;
    downloadingPdf = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private payrollService: PayrollService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService
    ) { }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            const idParam = params.get('id');
            const payrollIdParam = params.get('payrollId');
            this.employeeId = idParam ? parseInt(idParam, 10) : 0;
            this.payrollId = payrollIdParam ? parseInt(payrollIdParam, 10) : 0;
            this.loadPayroll();
        });
    }

    loadPayroll(): void {
        if (!this.payrollId) return;
        this.loading = true;
        this.loadError = false;
        this.spinner.show();
        this.payrollService.getPayrollById(this.payrollId).subscribe({
            next: (resp: any) => {
                this.payroll = resp;
                if (!this.employeeId && resp?.employee_id) this.employeeId = resp.employee_id;
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.payroll = null;
                this.loading = false;
                this.loadError = true;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el recibo de nómina', icon: 'error' });
            }
        });
    }

    get employee(): any {
        return this.payroll?.employee || null;
    }

    get isStamped(): boolean {
        return this.payroll?.cfdi_status === 'stamped';
    }

    get cfdiStatusLabel(): string {
        switch (this.payroll?.cfdi_status) {
            case 'stamped': return 'Timbrado';
            case 'error': return 'Error';
            case 'pending':
            default: return 'Pendiente';
        }
    }

    get earnings(): PayrollConcept[] {
        return this.parseConcepts(this.payroll?.earnings);
    }

    get deductions(): PayrollConcept[] {
        return this.parseConcepts(this.payroll?.deductions);
    }

    private parseConcepts(value: any): PayrollConcept[] {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    get fullName(): string {
        if (!this.employee) return '';
        return [this.employee.first_name, this.employee.last_name, this.employee.mother_last_name].filter(Boolean).join(' ');
    }

    get initials(): string {
        if (!this.employee) return '--';
        const a = String(this.employee.first_name || '').charAt(0);
        const b = String(this.employee.last_name || '').charAt(0);
        const value = `${a}${b}`.toUpperCase();
        return value || '--';
    }

    get statusIsActive(): boolean {
        return this.employee?.status !== 'terminated';
    }

    get statusLabel(): string {
        return this.statusIsActive ? 'Alta' : 'Baja';
    }

    goBackToList(): void {
        this.location.back();
    }

    goToEmployee(): void {
        this.router.navigateByUrl(`/employee/${this.employeeId}`);
    }

    downloadPdf(): void {
        if (!this.payrollId || this.downloadingPdf) return;
        this.downloadingPdf = true;
        this.payrollService.downloadPdf(this.payrollId).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `recibo_nomina_${this.payroll?.payroll_number || this.payrollId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                this.downloadingPdf = false;
            },
            error: () => {
                this.downloadingPdf = false;
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo descargar el PDF del recibo', icon: 'error' });
            }
        });
    }

    formatCurrency(value: any): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatNumber(value: any, decimals = 2): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    formatDateTime(value: any): string {
        if (!value) return '--';
        const normalized = String(value).replace(' ', 'T');
        const date = new Date(normalized);
        if (isNaN(date.getTime())) return String(value);
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
}
